import { formatBps, useAuthStore } from '@bemor/web-kit';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    StatusBadge,
} from '@bemor/web-kit/ui';
import { Link } from 'react-router-dom';

import type { RiskMonitoringDetail, RiskSummary } from '@/api/types';
import { P } from '@/lib/permissions';
import { useOne } from '@/lib/queries';
import { ADMIN_STATUSES } from '@/lib/statuses';
import { measureLabel } from '@/pages/risk/measures';
import { monitoringHref } from '@/pages/risk/monitoring';

/** The 1% chargeback line of the card networks (BM-125 decision 1), in basis points. */
const BREACH_BPS = 100;

function Ratio({
    label,
    bps,
    min,
    orders,
}: {
    label: string;
    bps: number | null;
    min: number;
    orders: number;
}) {
    const width = bps === null ? 0 : Math.min(100, (bps / BREACH_BPS) * 100);
    return (
        <div
            data-testid={`risk-ratio-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
            <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm">{label}</span>
                <span
                    className={
                        bps !== null && bps >= BREACH_BPS
                            ? 'text-destructive font-semibold tabular-nums'
                            : 'tabular-nums'
                    }
                >
                    {bps === null ? `— (under ${min} orders)` : formatBps(bps)}
                </span>
            </div>
            <div className="bg-muted mt-1 h-2 w-full rounded" aria-hidden>
                <div
                    className={
                        bps !== null && bps >= BREACH_BPS
                            ? 'bg-destructive h-2 rounded'
                            : 'bg-primary h-2 rounded'
                    }
                    style={{ width: `${width}%` }}
                />
            </div>
            <p className="text-fg-2 m-0 mt-1 text-xs">
                {orders} orders · the bar is full at 1%
            </p>
        </div>
    );
}

/** BM-125 slice 3: the merchant's monitoring state (Watch, Action), its applied measures and the way to Monitoring. */
function MonitoringLine({ merchantId }: { merchantId: string }) {
    const query = useOne<RiskMonitoringDetail>(
        `/staff/risk/monitoring/${merchantId}`,
    );
    const d = query.data?.data;
    if (!d) return null;
    const applied = d.measures.filter((m) => m.state === 'applied');
    return (
        <div
            className="flex flex-wrap items-center gap-2 text-sm md:col-span-2"
            data-testid="merchant-risk-monitoring"
        >
            <StatusBadge
                status={d.state}
                domain={ADMIN_STATUSES.riskMonitoring}
            />
            {applied.length === 0 ? (
                <span className="text-fg-2">No measures</span>
            ) : (
                applied.map((m) => (
                    <span key={m.id} className="text-fg-2">
                        {measureLabel(m)}
                    </span>
                ))
            )}
            <Link
                to={monitoringHref(merchantId)}
                className="ml-auto hover:underline"
            >
                Monitoring
            </Link>
        </div>
    );
}

/** BM-125 decisions 1 and 2 (E11): the merchant's chargeback and fraud ratios over the trailing 30 days and the calendar month. */
export function MerchantRiskCard({ merchantId }: { merchantId: string }) {
    const can = useAuthStore((s) => s.can);
    const query = useOne<RiskSummary>(
        can(P.reportsRead) ? '/staff/reports/risk-summary' : null,
        { merchant_id: merchantId },
    );
    if (!can(P.reportsRead)) return null;
    const summary = query.data?.data;
    return (
        <Card data-testid="merchant-risk">
            <CardHeader>
                <CardTitle className="text-sm font-medium">Risk</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {can(P.riskMonitoringRead) ? (
                    <MonitoringLine merchantId={merchantId} />
                ) : null}
                {summary ? (
                    summary.windows.map((w) => (
                        <div key={w.key} className="space-y-3">
                            <p className="m-0 text-xs font-medium tracking-wide uppercase">
                                {w.key === '30d'
                                    ? 'Last 30 days'
                                    : 'This month'}
                            </p>
                            <Ratio
                                label="Chargebacks"
                                bps={w.chargeback_ratio_bps}
                                min={w.min_orders}
                                orders={w.orders}
                            />
                            <Ratio
                                label="Fraud"
                                bps={w.fraud_ratio_bps}
                                min={w.min_orders}
                                orders={w.orders}
                            />
                        </div>
                    ))
                ) : (
                    <p className="text-fg-2 m-0 text-sm">Loading the ratios…</p>
                )}
            </CardContent>
        </Card>
    );
}
